import { lazyRoute, type LazyRoute } from './lib/lazyRoute';

export type RouteParams = Record<string, string>;
export type PageProps = { params: RouteParams };

type RouteDef = { pattern: string; component: LazyRoute<PageProps> };

/** Every route in the app. The router, the prerender step and the sitemap all read this. */
export const routeDefs: RouteDef[] = [
  { pattern: '/', component: lazyRoute(() => import('./pages/Home')) },
  { pattern: '/services', component: lazyRoute(() => import('./pages/Services')) },
  { pattern: '/services/:slug', component: lazyRoute(() => import('./pages/ServiceDetail')) },
  { pattern: '/portfolio', component: lazyRoute(() => import('./pages/Portfolio')) },
  { pattern: '/portfolio/:slug', component: lazyRoute(() => import('./pages/ProjectDetail')) },
  { pattern: '/about', component: lazyRoute(() => import('./pages/About')) },
  { pattern: '/journal', component: lazyRoute(() => import('./pages/Journal')) },
  { pattern: '/journal/:slug', component: lazyRoute(() => import('./pages/Article')) },
  { pattern: '/contact', component: lazyRoute(() => import('./pages/Contact')) },
  { pattern: '/privacy-policy', component: lazyRoute(() => import('./pages/PrivacyPolicy')) },
];

export const NotFoundPage = lazyRoute<PageProps>(() => import('./pages/NotFound'));

/** Source module of each route pattern, used by the prerender step to find chunks in the Vite manifest. */
export const routeModules: Record<string, string> = {
  '/': 'src/pages/Home.tsx',
  '/services': 'src/pages/Services.tsx',
  '/services/:slug': 'src/pages/ServiceDetail.tsx',
  '/portfolio': 'src/pages/Portfolio.tsx',
  '/portfolio/:slug': 'src/pages/ProjectDetail.tsx',
  '/about': 'src/pages/About.tsx',
  '/journal': 'src/pages/Journal.tsx',
  '/journal/:slug': 'src/pages/Article.tsx',
  '/contact': 'src/pages/Contact.tsx',
  '/privacy-policy': 'src/pages/PrivacyPolicy.tsx',
  '404': 'src/pages/NotFound.tsx',
};

export function matchPattern(pattern: string, path: string): RouteParams | null {
  const p = pattern.split('/').filter(Boolean);
  const s = path.split(/[?#]/)[0].split('/').filter(Boolean);
  if (p.length !== s.length) return null;
  const params: RouteParams = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(':')) params[p[i].slice(1)] = decodeURIComponent(s[i]);
    else if (p[i] !== s[i]) return null;
  }
  return params;
}

export function resolveRoute(path: string): { def: RouteDef; params: RouteParams } | null {
  for (const def of routeDefs) {
    const params = matchPattern(def.pattern, path);
    if (params) return { def, params };
  }
  return null;
}

/** Download a route's chunk ahead of navigation (and before hydration). */
export function preloadPath(path: string): Promise<unknown> {
  const match = resolveRoute(path);
  return match ? match.def.component.preload() : NotFoundPage.preload();
}
